import React, { useMemo } from 'react';

const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg', 'avif']);
const VIDEO_EXTENSIONS = new Set(['mp4', 'webm', 'ogg', 'mov', 'm4v']);
const AUDIO_EXTENSIONS = new Set(['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac']);

const cleanValue = (value) => String(value || '').trim();

const parseUnknownToArray = (value) => {
    if (Array.isArray(value)) {
        return value
            .map((item) => cleanValue(item))
            .filter((item) => item && item !== 'null' && item !== 'undefined');
    }

    if (value === null || value === undefined) {
        return [];
    }

    const normalized = cleanValue(value);
    if (!normalized || normalized === 'null' || normalized === 'undefined') {
        return [];
    }

    const looksLikeJson = normalized.startsWith('[') && normalized.endsWith(']');
    if (looksLikeJson) {
        try {
            const parsed = JSON.parse(normalized);
            if (Array.isArray(parsed)) {
                return parsed
                    .map((item) => cleanValue(item))
                    .filter((item) => item && item !== 'null' && item !== 'undefined');
            }
        } catch (error) {
            // Ignore JSON parsing errors and fallback to plain string.
        }
    }

    return [normalized];
};

const toAbsoluteAttachmentUrl = (value) => {
    const normalized = cleanValue(value);
    if (!normalized || normalized === 'null' || normalized === 'undefined') {
        return '';
    }

    if (/^https?:\/\//i.test(normalized) || normalized.startsWith('blob:') || normalized.startsWith('data:')) {
        return normalized;
    }

    const baseUrl = cleanValue(process.env.REACT_APP_BASE_URL).replace(/\/+$/, '');
    if (!baseUrl) {
        return normalized;
    }

    return normalized.startsWith('/') ? `${baseUrl}${normalized}` : `${baseUrl}/${normalized}`;
};

export const getChatAttachmentDownloadUrl = (value) => {
    const normalized = cleanValue(value);
    if (!normalized) {
        return '';
    }
    const separator = normalized.includes('?') ? '&' : '?';
    return `${normalized}${separator}download=1`;
};

const getExtension = (value) => {
    const normalized = cleanValue(value).toLowerCase().split('?')[0].split('#')[0];
    const slashIndex = normalized.lastIndexOf('/');
    const filePart = slashIndex >= 0 ? normalized.slice(slashIndex + 1) : normalized;
    const dotIndex = filePart.lastIndexOf('.');
    if (dotIndex === -1 || dotIndex === filePart.length - 1) {
        return '';
    }
    return filePart.slice(dotIndex + 1);
};

const getFileNameFromUrl = (value) => {
    const normalized = cleanValue(value).split('?')[0].split('#')[0];
    if (!normalized) {
        return '';
    }

    const trimmed = normalized.endsWith('/') ? normalized.slice(0, -1) : normalized;
    const slashIndex = trimmed.lastIndexOf('/');
    const candidate = slashIndex >= 0 ? trimmed.slice(slashIndex + 1) : trimmed;
    const lowerCandidate = candidate.toLowerCase();

    if (!candidate || lowerCandidate === 'attachment' || lowerCandidate === 'null' || lowerCandidate === 'undefined') {
        return '';
    }

    try {
        return decodeURIComponent(candidate);
    } catch (error) {
        return candidate;
    }
};

const inferAttachmentType = (fileName, fileUrl) => {
    const extension = getExtension(fileName) || getExtension(fileUrl);
    if (IMAGE_EXTENSIONS.has(extension)) return 'image';
    if (VIDEO_EXTENSIONS.has(extension)) return 'video';
    if (AUDIO_EXTENSIONS.has(extension)) return 'audio';
    if (extension === 'pdf') return 'pdf';
    return 'other';
};

const getAttachmentIcon = (type) => {
    if (type === 'image') return 'bx-image';
    if (type === 'video') return 'bx-video';
    if (type === 'audio') return 'bx-music';
    if (type === 'pdf') return 'bx-file-blank';
    return 'bx-file';
};

export const buildChatAttachments = (message = {}) => {
    const names = parseUnknownToArray(message?.file_name);
    const urls = parseUnknownToArray(message?.file_url).map((url) => toAbsoluteAttachmentUrl(url));
    const totalCount = Math.max(names.length, urls.length);

    if (!totalCount) {
        return [];
    }

    const seen = new Set();
    const attachments = [];

    for (let index = 0; index < totalCount; index += 1) {
        const rawName = cleanValue(names[index] || names[0] || '');
        const url = cleanValue(urls[index] || urls[0] || '');
        const inferredName = getFileNameFromUrl(url);

        if (!rawName && !url) {
            continue;
        }

        // Ignore placeholder URLs like "/v4/chat/attachment/" where no real file exists.
        if (url && !inferredName) {
            continue;
        }

        const name = rawName || inferredName;
        if (!name) {
            continue;
        }

        const dedupeKey = `${name}|${url}`;
        if (seen.has(dedupeKey)) {
            continue;
        }
        seen.add(dedupeKey);

        const type = inferAttachmentType(name, url);
        attachments.push({
            id: `${dedupeKey}|${index}`,
            name: name || `Attachment ${index + 1}`,
            url,
            type,
            iconClass: getAttachmentIcon(type),
        });
    }

    return attachments;
};

const renderAttachmentMedia = (attachment, compact) => {
    if (!attachment?.url) {
        return null;
    }

    if (attachment.type === 'image') {
        return (
            <a href={attachment.url} target="_blank" rel="noopener noreferrer" className="chat-attachment-media-link">
                <img src={attachment.url} alt={attachment.name} className="chat-attachment-thumb" />
            </a>
        );
    }

    if (attachment.type === 'video') {
        return (
            <video controls preload="metadata" className="chat-attachment-video">
                <source src={attachment.url} />
            </video>
        );
    }

    if (attachment.type === 'audio') {
        return <audio controls preload="metadata" className="chat-attachment-audio" src={attachment.url} />;
    }

    if (attachment.type === 'pdf' && !compact) {
        return <iframe title={attachment.name} src={attachment.url} className="chat-attachment-frame" />;
    }

    return null;
};

export const ChatAttachmentPreviewList = ({ message, compact = false, className = '' }) => {
    const attachments = useMemo(
        () => buildChatAttachments(message),
        [message?.file_name, message?.file_url]
    );

    if (!attachments.length) {
        return null;
    }

    const listClassName = `chat-attachment-list ${compact ? 'compact' : ''} ${className}`.trim();

    return (
        <div className={listClassName}>
            {attachments.map((attachment) => {
                const media = renderAttachmentMedia(attachment, compact);
                const hasInlinePreview = Boolean(media);

                return (
                    <div key={attachment.id} className="chat-attachment-card">
                        {hasInlinePreview ? media : (
                            <div className="chat-attachment-file">
                                <i className={`bx ${attachment.iconClass}`} />
                                <div className="chat-attachment-file-content">
                                    <div className="chat-attachment-file-title" title={attachment.name}>{attachment.name}</div>
                                    <div className="chat-attachment-file-subtitle">Preview unavailable</div>
                                </div>
                            </div>
                        )}

                        <div className="chat-attachment-meta">
                            <span className="chat-attachment-name" title={attachment.name}>{attachment.name}</span>
                            <span className="chat-attachment-actions">
                                {attachment.url && (
                                    <a
                                        href={attachment.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="chat-attachment-link"
                                    >
                                        Open
                                    </a>
                                )}
                                {attachment.url && (
                                    <a
                                        href={getChatAttachmentDownloadUrl(attachment.url)}
                                        className="chat-attachment-link"
                                        download={attachment.name}
                                    >
                                        Download
                                    </a>
                                )}
                            </span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
